import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDevMode } from '../../hooks/useDevMode';
import AppHeader from '../../components/AppHeader';
import BaseModal from '../../components/BaseModal';
import Icon from '@mdi/react';
import { mdiQrcode, mdiPencil, mdiRefresh, mdiDownload, mdiPrinterOutline, mdiTicketOutline, mdiContentSaveOutline, mdiCalendarRemove, mdiAlertCircleOutline, mdiShareOutline, mdiHelpCircle, mdiHelpCircleOutline } from '@mdi/js';
import TicketingHelpModal from '../../components/TicketingHelpModal';
import { toast } from 'react-toastify';
import { ticketApi } from '../../api/ticket';
import { formatCurrencyFromMinor } from '../../utils/currency';
import EmailReviewModal from '../../components/communications/EmailReviewModal';
import '../Common.css';
import '../Dashboard.css';
import '../header.css';
import './TicketingManage.css';

/**
 * Maps a live event's backend `saleStatus` to a TICKET-SALES badge shown next to the
 * "X tickets sold" line. This is a SEPARATE axis from the header "Live" pill (publish
 * status): an event stays Live even when its sales have ended. Mirrors the public landing
 * page's sale-state so an admin can spot a live event that isn't actually selling.
 */
const getSaleBadge = (ticket) => {
    switch (ticket.saleStatus) {
        case 'sold_out':
            return { label: 'Sold out', className: 'ticket-sale-badge sold-out' };
        case 'ended':
            return { label: 'Sales ended', className: 'ticket-sale-badge sales-ended' };
        case 'coming_soon':
            return { label: `On sale ${ticket.salesStartLabel || 'soon'}`, className: 'ticket-sale-badge coming-soon' };
        case 'on_sale':
            return { label: 'On sale', className: 'ticket-sale-badge on-sale' };
        // 'no_tickets' is unreachable on a Manage card (every card is a published ticketed
        // config; the invalid-window path is now blocked at publish) — no badge.
        default:
            return null;
    }
};

/**
 * TicketingManage Page - Ticketing Portal
 *
 * Manage existing event tickets and ticketing configurations
 * Displays published tickets in a grid with QR code access
 */
const TicketingManage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { isDevMode } = useDevMode();

    // Published tickets state (default load = upcoming/live + cancelled)
    const [publishedTickets, setPublishedTickets] = useState([]);
    const [isLoadingPublishedTickets, setIsLoadingPublishedTickets] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [helpIconHovered, setHelpIconHovered] = useState(false);

    // Past events are fetched on demand (kept off the initial load for speed)
    const [pastTickets, setPastTickets] = useState([]);
    const [isLoadingPastTickets, setIsLoadingPastTickets] = useState(false);
    const [showPastTickets, setShowPastTickets] = useState(false);
    const [showQRCodeModal, setShowQRCodeModal] = useState(false);
    const [selectedTicketQR, setSelectedTicketQR] = useState(null);
    const [isRegeneratingQR, setIsRegeneratingQR] = useState(false);

    // Draft management state
    const [showDraftModal, setShowDraftModal] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);

    // Print attendee list state
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [printAttendees, setPrintAttendees] = useState(null); // { attendees, eventDetails, totalTickets }
    const [isLoadingPrintList, setIsLoadingPrintList] = useState(false);
    const [printSortOrder, setPrintSortOrder] = useState("lastName"); // 'firstName' | 'lastName'

    // Cancel event state
    const [cancelTarget, setCancelTarget] = useState(null);
    const [cancelPreview, setCancelPreview] = useState(null);
    const [isLoadingCancelPreview, setIsLoadingCancelPreview] = useState(false);

    // Email review modal state (intercepts "Yes, Cancel Event" send)
    const [showCancelEmailReview, setShowCancelEmailReview] = useState(false);
    const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);

    // Draft management functions
    const fetchDrafts = async () => {
        try {
            setIsLoadingDrafts(true);
            console.log('Fetching drafts from API...');
            const draftList = await ticketApi.getDrafts();
            console.log(`Found ${draftList.length} drafts:`, draftList);
            setDrafts(draftList);
        } catch (error) {
            console.error('Error fetching drafts:', error);
            setDrafts([]);
        } finally {
            setIsLoadingDrafts(false);
        }
    };

    const handleLoadDraft = () => {
        setShowDraftModal(true);
        fetchDrafts();
    };

    const handleDraftSelect = (draftId) => {
        setShowDraftModal(false);
        navigate(`/create-tickets?draft=${draftId}`);
    };

    const handleCloseDraftModal = () => {
        setShowDraftModal(false);
        setDrafts([]);
    };

    // Cancel event handlers
    const handleCancelClick = async (ticket) => {
        setCancelTarget(ticket);
        setCancelPreview(null);
        setIsLoadingCancelPreview(true);
        try {
            const preview = await ticketApi.cancelEventPreview(ticket.id);
            setCancelPreview(preview);
        } catch (error) {
            toast.error(error.message || 'Failed to load cancellation preview');
            setCancelTarget(null);
        } finally {
            setIsLoadingCancelPreview(false);
        }
    };

    const handleCancelClose = () => {
        setCancelTarget(null);
        setCancelPreview(null);
    };

    const openCancelEmailReview = () => {
        setShowCancelEmailReview(true);
    };

    // After tier-2-consolidate-school-emails, EmailReviewModal in Mode 2a calls
    // onConfirm({ reason }). The reason text is forwarded to ticketApi.cancelEvent
    // which renders it inside the cancellation email template.
    const handleConfirmCancelWithEmail = async ({ reason } = {}) => {
        setIsConfirmingCancel(true);
        try {
            const refunds = (cancelPreview?.purchasers || [])
                .filter(p => p.suggestedRefund > 0)
                .map(p => ({ transactionId: p.transactionId, refundAmount: p.suggestedRefund }));
            const result = await ticketApi.cancelEvent(cancelTarget.id, reason || '', refunds);
            const refundErrors = result?.refundErrors || [];
            if (refundErrors.length > 0) {
                toast.success('Event cancelled');
                toast.warning(
                    `${refundErrors.length} of ${refunds.length} refund${refunds.length !== 1 ? 's' : ''} could not be processed. Check Stripe and refund these buyers manually.`,
                    { autoClose: false }
                );
            } else {
                toast.success('Event cancelled and refunds processed');
            }
            setShowCancelEmailReview(false);
            handleCancelClose();
            loadPublishedTickets();
        } catch (error) {
            toast.error(error.message || 'Failed to cancel event');
        } finally {
            setIsConfirmingCancel(false);
        }
    };

    // Print attendee list handlers
    const handlePrintAttendeeList = async (ticket) => {
        setPrintAttendees(null);
        setPrintSortOrder("lastName");
        setShowPrintModal(true);
        setIsLoadingPrintList(true);
        try {
            const data = await ticketApi.getAttendeeList(ticket.id);
            setPrintAttendees(data);
        } catch (error) {
            toast.error(error.message || "Failed to load attendee list");
            setShowPrintModal(false);
        } finally {
            setIsLoadingPrintList(false);
        }
    };

    const closePrintModal = () => {
        setShowPrintModal(false);
        setPrintAttendees(null);
    };

    const getSortedAttendees = () => {
        if (!printAttendees?.attendees) return [];
        return [...printAttendees.attendees].sort((a, b) => {
            if (printSortOrder === "lastName") {
                const aLast = a.lastName || a.firstName;
                const bLast = b.lastName || b.firstName;
                return aLast.localeCompare(bLast);
            }
            return a.firstName.localeCompare(b.firstName);
        });
    };

    // Load upcoming/live events + cancelled (the default page load — past events excluded)
    const loadPublishedTickets = useCallback(async () => {
        if (!user?.organisationId) {
            console.log("No organisationId available, skipping published tickets load");
            return;
        }

        try {
            setIsLoadingPublishedTickets(true);
            const publishedList = await ticketApi.getPublishedAndCancelled('upcoming');
            setPublishedTickets(publishedList);
        } catch (error) {
            console.error("Error loading published tickets:", error);
            setPublishedTickets([]);
        } finally {
            setIsLoadingPublishedTickets(false);
        }
    }, [user?.organisationId]);

    // Fetch past (ended) events on demand — only when the organiser asks to see them
    const handleShowPastTickets = useCallback(async () => {
        setShowPastTickets(true);
        try {
            setIsLoadingPastTickets(true);
            const pastList = await ticketApi.getPublishedAndCancelled('past');
            setPastTickets(pastList);
        } catch (error) {
            console.error("Error loading past tickets:", error);
            toast.error(error.message || 'Failed to load past events');
            setPastTickets([]);
        } finally {
            setIsLoadingPastTickets(false);
        }
    }, []);

    // Load tickets on mount
    useEffect(() => {
        loadPublishedTickets();
    }, [loadPublishedTickets]);

    const handleShareLink = (ticket) => {
        if (!ticket.correspondingEventId) return;
        const baseUrl = isDevMode ? 'http://localhost:3000' : 'https://dancelocale.com';
        const url = `${baseUrl}/events/${ticket.correspondingEventId}`;
        navigator.clipboard.writeText(url)
            .then(() => toast.success('Link copied to clipboard!'))
            .catch(() => toast.error('Failed to copy link'));
    };

    // Show QR code modal
    const showQRCode = async ticket => {
        try {
            const qrCodeData = await ticketApi.getQRCode(ticket.id);
            setSelectedTicketQR({
                qrCode: qrCodeData.qrCode,
                eventTitle: qrCodeData.eventTitle,
                ticket: ticket,
            });
            setShowQRCodeModal(true);
        } catch (error) {
            console.error("QR code error:", error);
            alert("Failed to retrieve QR code. Please try again.");
        }
    };

    // Close QR code modal
    const closeQRCodeModal = () => {
        setShowQRCodeModal(false);
        setSelectedTicketQR(null);
    };

    // Regenerate QR code
    const handleRegenerateQRCode = async () => {
        try {
            setIsRegeneratingQR(true);

            await ticketApi.regenerateQR(selectedTicketQR.ticket.id);
            const qrCodeData = await ticketApi.getQRCode(selectedTicketQR.ticket.id);

            setSelectedTicketQR({
                ...selectedTicketQR,
                qrCode: qrCodeData.qrCode,
            });
            alert("QR code regenerated successfully!");
        } catch (error) {
            console.error("Regenerate QR error:", error);
            alert("Failed to regenerate QR code. Please try again.");
        } finally {
            setIsRegeneratingQR(false);
        }
    };

    return (
        <div className="dashboard-container org-console">
            <AppHeader />

            {/* Main Content */}
            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button
                                onClick={() => navigate('/ticketing')}
                                className="back-nav-btn"
                            >
                                ← Ticketing
                            </button>
                            <h1 className="sub-portal-header-title">Manage Event Tickets</h1>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '8px' }}>
                            <p className="sub-portal-header-subtitle" style={{ margin: 0 }}>
                                View and manage your published event tickets, access QR codes for check-in.
                                {isDevMode && (
                                    <span className="ticketing-manage-dev-badge dev">
                                        🧪 Dev Mode
                                    </span>
                                )}
                            </p>
                            <button
                                className="classes-help-trigger"
                                onClick={() => setHelpOpen(true)}
                                onMouseEnter={() => setHelpIconHovered(true)}
                                onMouseLeave={() => setHelpIconHovered(false)}
                                aria-label="Event Tickets Help"
                                title="How It Works"
                            >
                                <Icon
                                    path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                                    size="1.5rem"
                                    color="var(--dl-color-purple-default)"
                                />
                            </button>
                        </div>
                    </div>

                    <div className="ticketing-manage-content">
                        {/* Action Buttons */}
                        <div className="ticketing-manage-section">
                            <div className="ticketing-manage-actions">
                                <button onClick={() => navigate('/create-tickets')} className="ticketing-manage-btn-create">
                                    <Icon path={mdiTicketOutline} size={0.8} />
                                    Create New Event Tickets
                                </button>
                                <button onClick={handleLoadDraft} className="ticketing-manage-btn-draft">
                                    <Icon path={mdiContentSaveOutline} size={0.8} />
                                    Load Existing Draft
                                </button>
                            </div>
                        </div>

                        {/* Published Tickets Section */}
                        <div className="ticketing-manage-section">
                            <h2 className="ticketing-manage-section-title">Published Events</h2>
                            <p className="ticketing-manage-section-desc">Manage your live ticketed events and QR codes for check-in.</p>

                            {isLoadingPublishedTickets ? (
                                <div className="loading-state">Loading published events...</div>
                            ) : publishedTickets.length > 0 ? (
                                (() => {
                                    // Use endDateTime if available, otherwise startDateTime + 6hr buffer
                                    // so late-night events don't flip to "past" until the next morning
                                    const now = new Date();
                                    const isPast = (t) => {
                                        const end = t.eventDetails?.endDateTime;
                                        const start = t.eventDetails?.startDateTime;
                                        if (end) return new Date(end) < now;
                                        if (start) return new Date(new Date(start).getTime() + 6 * 60 * 60 * 1000) < now;
                                        return false;
                                    };
                                    // The default fetch ('upcoming') returns future/live events + all
                                    // cancelled; past events are loaded separately into pastTickets on demand.
                                    // The isPast guard on `upcoming` is belt-and-suspenders against tiny
                                    // client/server clock skew at the boundary.
                                    const upcoming = publishedTickets.filter(t => t.status !== 'cancelled' && !isPast(t));
                                    const cancelled = publishedTickets.filter(t => t.status === 'cancelled');

                                    const renderActiveCard = (ticket) => {
                                        const saleBadge = getSaleBadge(ticket);
                                        return (
                                        <div key={ticket.id || ticket.eventId} className="ticket-card">
                                            <div className="ticket-card-header">
                                                <h5>{ticket.eventDetails?.title || "Untitled Event"}</h5>
                                                <span className={`ticket-status published${ticket.hasUnpublishedChanges ? ' has-unpublished' : ''}`}>
                                                    {ticket.hasUnpublishedChanges ? "Live - unpublished edits" : "Live"}
                                                </span>
                                            </div>
                                            <div className="ticket-card-details">
                                                <div className="ticket-card-date-row">
                                                    <p className="event-date" style={{ margin: 0 }}>
                                                        {ticket.eventDetails?.startDateTime
                                                            ? new Date(ticket.eventDetails.startDateTime).toLocaleDateString("en-AU", {
                                                                  weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                                                              })
                                                            : "Date TBA"}
                                                    </p>
                                                    <button
                                                        onClick={() => handleCancelClick(ticket)}
                                                        className="ticket-cancel-icon"
                                                        title="Cancel event and refund ticket holders"
                                                        aria-label="Cancel event"
                                                    >
                                                        <Icon path={mdiCalendarRemove} size={0.9} />
                                                    </button>
                                                </div>
                                                <p className="event-location">{ticket.eventDetails?.location?.description || "Location TBA"}</p>
                                                <div className="ticket-card-sold-row">
                                                    <span className="ticket-card-sold-count">{ticket.ticketsSold || 0} tickets sold</span>
                                                    {saleBadge && <span className={saleBadge.className}>{saleBadge.label}</span>}
                                                </div>
                                            </div>
                                            <div className="ticket-card-actions">
                                                <div className="ticket-card-actions-row">
                                                    <button onClick={() => showQRCode(ticket)} className="btn-turquoise btn-small" title="Generate QR code for event check-in">
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Icon path={mdiQrcode} size={0.7} />QR</span>
                                                    </button>
                                                    <button onClick={() => navigate(`/create-tickets?draft=${ticket.id}&mode=edit`)} className="btn-turquoise-outline btn-small" title="Edit event details">
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Icon path={mdiPencil} size={0.7} />Edit</span>
                                                    </button>
                                                    <button onClick={() => handleShareLink(ticket)} className="btn-turquoise-outline btn-small" title="Copy shareable event link">
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Icon path={mdiShareOutline} size={0.7} />Share</span>
                                                    </button>
                                                    <button onClick={() => handlePrintAttendeeList(ticket)} className="btn-turquoise-outline btn-small" title="Print attendee list for door check-in">
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Icon path={mdiPrinterOutline} size={0.7} />Print</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    };

                                    const renderCancelledCard = (ticket) => (
                                        <div key={ticket.id || ticket.eventId} className="ticket-card past">
                                            <div className="ticket-card-header">
                                                <h5>{ticket.eventDetails?.title || "Untitled Event"}</h5>
                                                <span className="ticket-status cancelled">Cancelled</span>
                                            </div>
                                            <div className="ticket-card-details">
                                                <div className="ticket-card-date-row">
                                                    <p className="event-date" style={{ margin: 0 }}>
                                                        {ticket.eventDetails?.startDateTime
                                                            ? new Date(ticket.eventDetails.startDateTime).toLocaleDateString("en-AU", {
                                                                  weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                                                              })
                                                            : "Date TBA"}
                                                    </p>
                                                    {ticket.ticketsSold > 0 && (
                                                        <span className="ticket-card-sold-count">{ticket.ticketsSold} refunded</span>
                                                    )}
                                                </div>
                                                <p className="event-location">{ticket.eventDetails?.location?.description || "Location TBA"}</p>
                                                {ticket.cancellationReason && (
                                                    <div className="ticket-card-cancelled-info">
                                                        <span className="cancelled-reason">Reason: {ticket.cancellationReason}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );

                                    return (
                                        <>
                                            {upcoming.length > 0 && (
                                                <div className="tickets-grid">{upcoming.map(renderActiveCard)}</div>
                                            )}
                                            {cancelled.length > 0 && (
                                                <>
                                                    <div className="ticketing-manage-events-divider">Cancelled</div>
                                                    <div className="tickets-grid">{cancelled.map(renderCancelledCard)}</div>
                                                </>
                                            )}
                                        </>
                                    );
                                })()
                            ) : (
                                <div className="empty-state">
                                    <p>No upcoming events.</p>
                                    <p>Create a ticketed event, or show past events below.</p>
                                </div>
                            )}

                            {/* Past events — fetched on demand so they stay off the initial page load */}
                            {!showPastTickets ? (
                                <button
                                    type="button"
                                    className="btn-turquoise-outline btn-small ticketing-manage-show-past-btn"
                                    onClick={handleShowPastTickets}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Icon path={mdiRefresh} size={0.7} />Show past events
                                    </span>
                                </button>
                            ) : (
                                <>
                                    <div className="ticketing-manage-events-divider">Past Events</div>
                                    {isLoadingPastTickets ? (
                                        <div className="loading-state">Loading past events...</div>
                                    ) : pastTickets.length > 0 ? (
                                        <div className="tickets-grid">
                                            {pastTickets.map((ticket) => (
                                                <div key={ticket.id || ticket.eventId} className="ticket-card past">
                                                    <div className="ticket-card-header">
                                                        <h5>{ticket.eventDetails?.title || "Untitled Event"}</h5>
                                                        <span className="ticket-status ended">Ended</span>
                                                    </div>
                                                    <div className="ticket-card-details">
                                                        <div className="ticket-card-date-row">
                                                            <p className="event-date" style={{ margin: 0 }}>
                                                                {ticket.eventDetails?.startDateTime
                                                                    ? new Date(ticket.eventDetails.startDateTime).toLocaleDateString("en-AU", {
                                                                          weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                                                                      })
                                                                    : "Date TBA"}
                                                            </p>
                                                            {ticket.ticketsSold > 0 && (
                                                                <span className="ticket-card-sold-count">{ticket.ticketsSold} tickets sold</span>
                                                            )}
                                                        </div>
                                                        <p className="event-location">{ticket.eventDetails?.location?.description || "Location TBA"}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="ticketing-manage-section-desc">No past events.</p>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Info Section - two column */}
                        <div className="ticketing-manage-section">
                            <div className="ticketing-manage-info-grid">
                                <div>
                                    <h2 className="ticketing-manage-section-title">Quick Actions</h2>
                                    <ul className="ticketing-manage-info-list">
                                        <li>Click "QR Code" to view and print event check-in codes</li>
                                        <li>Click "Edit" to modify event details and ticket types</li>
                                        <li>Monitor ticket sales and check-ins in real-time</li>
                                    </ul>
                                </div>
                                <div>
                                    <h2 className="ticketing-manage-section-title">Features</h2>
                                    <ul className="ticketing-manage-info-list">
                                        <li>QR code check-in system</li>
                                        <li>Multiple ticket types and pricing</li>
                                        <li>Real-time ticket inventory</li>
                                        <li>Download/Print QR codes</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* QR Code Modal */}
            <BaseModal
                isOpen={showQRCodeModal && !!selectedTicketQR}
                onClose={closeQRCodeModal}
                title="Event Check-in QR Code"
                icon={mdiQrcode}
                maxWidth={640}
                footer={
                    <>
                        <button className="base-modal-cancel-btn" style={{ marginRight: 'auto' }} onClick={closeQRCodeModal}>
                            Close
                        </button>
                        <button
                            className="base-modal-save-btn ticket-modal-btn-turquoise"
                            onClick={() => {
                                closeQRCodeModal();
                                handlePrintAttendeeList(selectedTicketQR.ticket);
                            }}
                        >
                            <Icon path={mdiPrinterOutline} size={0.8} />
                            Print Attendee
                        </button>
                        <button
                            className="base-modal-save-btn ticket-modal-btn-turquoise"
                            onClick={() => {
                                const link = document.createElement("a");
                                link.download = `${selectedTicketQR?.eventTitle || "event"}-checkin-qr.png`;
                                link.href = selectedTicketQR?.qrCode;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}>
                            <Icon path={mdiDownload} size={0.8} />
                            Download
                        </button>
                        <button
                            className="base-modal-save-btn"
                            onClick={() => {
                                const printWindow = window.open("", "_blank");
                                printWindow.document.write(`
                                    <html>
                                    <head>
                                        <title>Event Check-in QR Code - ${selectedTicketQR?.eventTitle}</title>
                                        <style>
                                            @page { size: A4 portrait; margin: 20mm; }
                                            body {
                                                margin: 0;
                                                font-family: Arial, sans-serif;
                                                text-align: center;
                                            }
                                            .container {
                                                display: flex;
                                                flex-direction: column;
                                                align-items: center;
                                                padding-top: 32px;
                                            }
                                            h1 {
                                                font-size: 30px;
                                                font-weight: 700;
                                                margin: 0 0 6px;
                                                color: #451f78;
                                            }
                                            .label {
                                                font-size: 13px;
                                                color: #888;
                                                margin: 0 0 28px;
                                                text-transform: uppercase;
                                                letter-spacing: 1px;
                                            }
                                            img {
                                                width: 320px;
                                                height: 320px;
                                            }
                                            .instruction {
                                                margin: 0 0 20px;
                                                font-size: 16px;
                                                color: #555;
                                            }
                                        </style>
                                    </head>
                                    <body>
                                        <div class="container">
                                            <h1>${selectedTicketQR?.eventTitle}</h1>
                                            <p class="label">Event Check-in QR Code</p>
                                            <p class="instruction">Scan with Dance Localé mobile app for check-in</p>
                                            <img src="${selectedTicketQR?.qrCode}" alt="QR Code" />
                                        </div>
                                    </body>
                                    </html>
                                `);
                                printWindow.document.close();
                                printWindow.focus();
                                setTimeout(() => {
                                    printWindow.print();
                                    printWindow.close();
                                }, 250);
                            }}>
                            <Icon path={mdiPrinterOutline} size={0.8} />
                            Print QR
                        </button>
                    </>
                }
            >
                {selectedTicketQR && (
                    <div className="qr-code-content">
                        <div className="event-info">
                            <h3>{selectedTicketQR.eventTitle}</h3>
                            <p>Display this QR code at your event entrance for attendee check-in</p>
                        </div>

                        <div className="qr-code-display">
                            <img
                                src={selectedTicketQR.qrCode}
                                alt="Event Check-in QR Code"
                                className="qr-code-image"
                            />
                        </div>

                        <div className="qr-code-instructions">
                            <h4>How to use:</h4>
                            <ol>
                                <li>Print or display this QR code at your event entrance</li>
                                <li>Ticket holders scan the code with the Dance Localé mobile app</li>
                                <li>App confirms their ticket and marks them as checked in</li>
                                <li>You can monitor check-ins in real-time on your dashboard</li>
                            </ol>
                        </div>

                        <div className="qr-code-troubleshooting">
                            <h4>Troubleshooting QR Code Scanning</h4>
                            <p>
                                If attendees are having trouble scanning the QR code, you can regenerate it.
                                The QR code is valid from midnight on the first event day until the event ends. Regenerating creates a fresh code that works immediately.
                            </p>
                            <button
                                className="btn-purple"
                                onClick={handleRegenerateQRCode}
                                disabled={isRegeneratingQR}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Icon path={mdiRefresh} size={0.8} />
                                    {isRegeneratingQR ? "Regenerating..." : "Regenerate QR Code"}
                                </span>
                            </button>
                        </div>
                    </div>
                )}
            </BaseModal>

            {/* Draft Selection Modal */}
            <BaseModal
                isOpen={showDraftModal}
                onClose={handleCloseDraftModal}
                title="Select Draft to Continue"
                footer={
                    <button className="base-modal-cancel-btn" onClick={handleCloseDraftModal}>
                        Cancel
                    </button>
                }
            >
                {isLoadingDrafts ? (
                    <div className="loading-state">
                        <p>Loading drafts...</p>
                    </div>
                ) : drafts.length === 0 ? (
                    <div className="empty-state">
                        <p>No drafts found. Create a new event ticket to get started.</p>
                        <button
                            onClick={() => {
                                handleCloseDraftModal();
                                navigate('/create-tickets');
                            }}
                            className="btn-orange"
                        >
                            Create New Event
                        </button>
                    </div>
                ) : (
                    <div className="drafts-list">
                        {drafts.map((draft) => (
                            <div key={draft.id} className="draft-item" onClick={() => handleDraftSelect(draft.id)}>
                                <div className="draft-info">
                                    <h4>{draft.eventDetails?.title || 'Untitled Event'}</h4>
                                    <p className="draft-location">
                                        {draft.eventDetails?.location?.description || 'Location not set'}
                                    </p>
                                    <p className="draft-date">
                                        {draft.eventDetails?.startDateTime
                                            ? new Date(draft.eventDetails.startDateTime).toLocaleString()
                                            : 'Date not set'}
                                    </p>
                                    <p className="draft-step">Step: {draft.currentStep || 1}</p>
                                    <p className="draft-status">
                                        Last saved: {draft.lastSaved ? new Date(draft.lastSaved).toLocaleString() : 'Unknown'}
                                    </p>
                                </div>
                                <button
                                    className="btn-turquoise btn-small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDraftSelect(draft.id);
                                    }}
                                >
                                    Continue →
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </BaseModal>

            {/* Cancel Event Preview Modal */}
            <BaseModal
                isOpen={!!cancelTarget && !showCancelEmailReview}
                onClose={handleCancelClose}
                title="Cancel Event"
                icon={mdiCalendarRemove}
                iconColor="#ef4444"
                maxWidth="600px"
                footer={
                    cancelPreview && !isLoadingCancelPreview ? (
                        <>
                            <button className="base-modal-cancel-btn" onClick={handleCancelClose}>
                                Keep Event
                            </button>
                            <button
                                className="base-modal-save-btn ticketing-cancel-preview-save-btn"
                                onClick={openCancelEmailReview}
                            >
                                Draft Cancellation Email
                            </button>
                        </>
                    ) : null
                }
            >
                {isLoadingCancelPreview ? (
                    <div className="loading-state">Loading cancellation details...</div>
                ) : cancelPreview ? (
                    <div className="cancel-preview-content">
                        <div className="cancel-preview-warning">
                            <Icon path={mdiAlertCircleOutline} size={0.8} color="#ef4444" />
                            <p>This will cancel <strong>{cancelPreview.eventTitle}</strong> and refund all ticket purchasers.</p>
                        </div>

                        {cancelPreview.purchasers.length > 0 ? (
                            <>
                                <div className="cancel-preview-summary">
                                    <span>{cancelPreview.attendeeCount} ticket(s)</span>
                                    <span>{cancelPreview.purchasers.length} purchaser(s)</span>
                                    <span>Total refund: {formatCurrencyFromMinor(cancelPreview.totalRefundAmount, cancelPreview.currency)}</span>
                                </div>

                                <div className="cancel-preview-purchasers">
                                    {cancelPreview.purchasers.map((p) => (
                                        <div key={p.transactionId} className="cancel-preview-purchaser-row">
                                            <span className="cancel-preview-purchaser-name">{p.purchaserName}</span>
                                            {p.isGuest && <span className="cancel-preview-guest-badge">Guest</span>}
                                            {p.hasCheckedIn && <span className="cancel-preview-checkedin-badge">Checked In</span>}
                                            <span className="cancel-preview-purchaser-tickets">{p.ticketCount} ticket(s) — {p.ticketTypes.join(', ')}</span>
                                            <span className="cancel-preview-purchaser-refund">{formatCurrencyFromMinor(p.suggestedRefund, p.currency)}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p className="cancel-preview-no-sales">No tickets have been sold for this event. The event will be cancelled with no refunds needed.</p>
                        )}

                        {cancelPreview.freeTicketCount > 0 && (
                            <p className="cancel-preview-free-note">
                                {cancelPreview.freeTicketCount} free ticket(s) will be cancelled (no refund needed).
                            </p>
                        )}

                    </div>
                ) : null}
            </BaseModal>

            {/* Email review modal — "Confirm Send" is the final destructive action */}
            <EmailReviewModal
                isOpen={showCancelEmailReview}
                emailType="eventCancellation"
                templateData={{
                    eventName: cancelPreview?.eventTitle || cancelTarget?.title || '',
                    refundLine: cancelPreview?.totalRefundAmount > 0
                        ? `<p>A refund of <strong>${formatCurrencyFromMinor(cancelPreview.totalRefundAmount, cancelPreview.currency)}</strong> will be processed automatically.</p>`
                        : '',
                }}
                onConfirm={handleConfirmCancelWithEmail}
                onCancel={() => setShowCancelEmailReview(false)}
                isConfirming={isConfirmingCancel}
                confirmingLabel="Cancelling…"
                dangerConfirm
                warningMessage="This will permanently cancel event and cannot be undone."
            />

            {/* Print Attendee List Modal */}
            <BaseModal
                isOpen={showPrintModal}
                onClose={closePrintModal}
                title="Print Attendee List"
                icon={mdiPrinterOutline}
                maxWidth="700px"
                footer={
                    <>
                        <button className="base-modal-cancel-btn" style={{ marginRight: 'auto' }} onClick={closePrintModal}>
                            Close
                        </button>
                        {printAttendees && (
                            <button
                                className="base-modal-save-btn ticket-print-list-save-btn"
                                onClick={() => {
                                    const style = document.createElement('style');
                                    style.id = 'attendee-list-page-style';
                                    style.innerHTML = '@page { size: A4 portrait; margin: 15mm; }';
                                    document.head.appendChild(style);
                                    window.print();
                                    const cleanup = () => {
                                        document.getElementById('attendee-list-page-style')?.remove();
                                        window.removeEventListener('afterprint', cleanup);
                                    };
                                    window.addEventListener('afterprint', cleanup);
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Icon path={mdiPrinterOutline} size={0.8} />
                                    Print
                                </span>
                            </button>
                        )}
                    </>
                }
            >
                {isLoadingPrintList ? (
                    <div className="loading-state">Loading attendee list...</div>
                ) : printAttendees ? (
                    <div className="ticket-print-list">
                        {/* Sort controls — screen only */}
                        <div className="ticket-print-list-controls">
                            <span className="ticket-print-list-sort-label">Sort by:</span>
                            <label className="ticket-print-list-sort-option">
                                <input
                                    type="radio"
                                    name="printSort"
                                    value="lastName"
                                    checked={printSortOrder === "lastName"}
                                    onChange={() => setPrintSortOrder("lastName")}
                                />
                                Last name A-Z
                            </label>
                            <label className="ticket-print-list-sort-option">
                                <input
                                    type="radio"
                                    name="printSort"
                                    value="firstName"
                                    checked={printSortOrder === "firstName"}
                                    onChange={() => setPrintSortOrder("firstName")}
                                />
                                First name A-Z
                            </label>
                        </div>

                        {/* Printable region */}
                        <div className="ticket-print-list-print-region">
                            {/* Event header */}
                            <div className="ticket-print-list-header">
                                <h1 className="ticket-print-list-event-title">
                                    {printAttendees.eventDetails.title}
                                </h1>
                                {printAttendees.eventDetails.startDateTime && (
                                    <p className="ticket-print-list-event-date">
                                        {new Date(printAttendees.eventDetails.startDateTime).toLocaleDateString("en-AU", {
                                            weekday: "long",
                                            day: "numeric",
                                            month: "long",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </p>
                                )}
                                <div className="ticket-print-list-venue-row">
                                    <p className="ticket-print-list-event-venue">
                                        {printAttendees.eventDetails.location || ""}
                                    </p>
                                    <span className="ticket-print-list-header-total">
                                        {printAttendees.totalTickets} ticket{printAttendees.totalTickets !== 1 ? "s" : ""}
                                    </span>
                                </div>
                            </div>

                            {/* Attendee table */}
                            <table className="ticket-print-list-table">
                                <thead>
                                    <tr>
                                        <th className="ticket-print-list-th-num">#</th>
                                        <th className="ticket-print-list-th-lastname">Last Name</th>
                                        <th className="ticket-print-list-th-firstname">First Name</th>
                                        <th className="ticket-print-list-th-check">Check</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getSortedAttendees().map((attendee, index) => (
                                        <tr
                                            key={index}
                                            className={`ticket-print-list-row${index % 2 === 1 ? " ticket-print-list-row-alt" : ""}`}
                                        >
                                            <td className="ticket-print-list-cell-num">{index + 1}</td>
                                            <td className="ticket-print-list-cell-name">{attendee.lastName || ""}</td>
                                            <td className="ticket-print-list-cell-name">{attendee.firstName || ""}</td>
                                            <td className="ticket-print-list-cell-check">
                                                <div className="ticket-print-list-checkbox" />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                        </div>
                    </div>
                ) : null}
            </BaseModal>

            <TicketingHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
        </div>
    );
};

export default TicketingManage;
